import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Sparkles,
  Clock,
  Calendar,
  Loader2,
  ArrowRight,
  Check,
} from "lucide-react";
import { toast } from "sonner";
import { createCheckout, api } from "@/lib/api";
import PlanRating from "@/components/PlanRating";
import PricingQuote from "@/components/PricingQuote";
import MobileTryBar from "@/components/MobileTryBar";
import { useAuth, startGoogleLogin } from "@/lib/auth";
import { useEntitlements } from "@/lib/entitlements";
import SiteHeader from "@/components/SiteHeader";
import { PageMeta, SITE_URL, OG_IMAGE } from "@/lib/seo";
import ROUTE_META from "@/lib/routeMeta.json";
import PlanComparison from "@/components/PlanComparison";
import ReviewBadgeStrip from "@/components/ReviewBadgeStrip";

// Keep these lookup keys in sync with /app/backend/payments/_common.py.
const QUARTERLY_LOOKUP = "umbra_attending_quarterly";
const YEARLY_LOOKUP = "umbra_companion_yearly";
const MONTHLY_PROMO_LOOKUP = "umbra_monthly_promo";

const TIERS = [
  {
    key: MONTHLY_PROMO_LOOKUP,
    testid: "pricing-founding-monthly",
    eyebrow: "Founding Monthly · sign up through Dec 31, 2026",
    name: "Founding Monthly",
    price: "$9.99",
    cadence: "per month",
    icon: Clock,
    highlight: false,
    tagline:
      "The lowest door, honoring the earliest listeners.",
    perks: [
      "Full workbook access while active",
      "Unlimited Mirror reflections & narrator voice",
      "3 ebooks per month from the library",
      "Locked at $9.99/mo through Dec 31, 2027 — then $12.99/mo",
      "Cancel anytime",
    ],
    anchor: "Less than one therapy copay a month — and a printed workbook's prompts run out on the last page.",
    cta: "Start founding",
    subnote: "Founding sign-ups close Dec 31, 2026 · after that: $12.99/mo Monthly.",
  },
  {
    key: QUARTERLY_LOOKUP,
    testid: "pricing-quarterly",
    eyebrow: "The Attending · quarterly rhythm",
    name: "Quarterly Companion",
    price: "$29.99",
    cadence: "every 3 months",
    icon: Sparkles,
    highlight: false,
    tagline:
      "$9.99/mo effective — 25% off Monthly. A season at a time.",
    perks: [
      "Full workbook access while active",
      "Unlimited Mirror reflections & Whisper transcription",
      "3 ebooks per quarter from the library",
      "Break-even pricing on printed chapter books",
      "Cancel at the end of any quarter",
    ],
    anchor: "Three months of speaking your answers costs less than a single therapy copay.",
    cta: "Begin the season",
  },
  {
    key: YEARLY_LOOKUP,
    testid: "pricing-yearly",
    eyebrow: "The Companion · yearly · best value",
    name: "Yearly Companion",
    price: "$89.99",
    cadence: "per year",
    icon: Calendar,
    highlight: true,
    tagline:
      "$7.50/mo effective — 42% off Monthly. The most generous door.",
    perks: [
      "Full workbook access all year",
      "Unlimited Mirror reflections & narrator voice",
      "12 ebooks per year — one every month",
      "Priority reflections and one free printed chapter book",
      "Cancel any renewal",
    ],
    anchor: "A month of speaking your answers costs less than a single therapy copay. A one-time $21.99 workbook gives you prompts; this gives you a year of someone listening back.",
    cta: "Save with yearly",
  },
];

const PRODUCT_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "Umbra · voice-first shadow work journal",
  description: "Seven chapters of guided shadow work you speak instead of write, with a gentle AI Mirror reflection on every answer.",
  image: OG_IMAGE,
  brand: { "@type": "Brand", name: "Umbra" },
  url: `${SITE_URL}/pricing`,
  offers: [
    { name: "Founding Monthly", price: "9.99" },
    { name: "Quarterly Companion", price: "29.99" },
    { name: "Yearly Companion", price: "89.99" },
  ].map((o) => ({ "@type": "Offer", ...o, priceCurrency: "USD", availability: "https://schema.org/InStock", url: `${SITE_URL}/pricing` })),
};

export default function Pricing() {
  const { user } = useAuth();
  const { entitlements } = useEntitlements();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const [badge, setBadge] = useState(null);

  useEffect(() => {
    api.get("/site/review-badge").then((r) => setBadge(r.data.badge)).catch(() => {});
  }, []);

  const startCheckout = async (lookupKey) => {
    if (!user) {
      startGoogleLogin();
      return;
    }
    try {
      setBusy(lookupKey);
      const { checkout_url } = await createCheckout(lookupKey);
      window.location.href = checkout_url;
    } catch (e) {
      console.error(e);
      toast.error("Could not open checkout. Please try again.");
      setBusy(null);
    }
  };

  const alreadySubscribed =
    entitlements &&
    (entitlements.subscription_active ||
      entitlements.tier === "listener_lifetime" ||
      entitlements.tier === "grandfathered_lifetime" ||
      entitlements.tier === "admin");


  return (
    <div className="relative min-h-screen bg-obsidian-900 overflow-hidden">
      <PageMeta
        {...ROUTE_META["/pricing"]}
        path="/pricing"
        jsonLd={[PRODUCT_JSONLD]}
      />
      <SiteHeader />
      <div className="absolute inset-0 opacity-10 pointer-events-none bg-gradient-to-b from-terracotta/20 via-transparent to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 md:px-16 pt-12 sm:pt-16 md:pt-28 pb-20 sm:pb-24">
        {/* Eyebrow + heading */}
        <div className="max-w-3xl">
          <p
            className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta mb-6"
            data-testid="pricing-eyebrow"
          >
            Pricing · three plans, one workbook
          </p>
          <h1
            className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-bone-100"
            data-testid="pricing-hero-title"
          >
            Choose the tempo{" "}
            <em className="text-terracotta not-italic">that fits</em> the work
            you're being called to.
          </h1>
          <p className="mt-6 sm:mt-8 font-sans text-base sm:text-lg text-bone-300 leading-relaxed max-w-2xl">
            Every plan opens the same seven chapters. What differs is how long
            you stay and how many keepsakes you carry home. Cancel within 7 days
            of any charge — no more billing.
          </p>

          {entitlements?.book_credit_cents > 0 && (
            <BookCreditNudge
              cents={entitlements.book_credit_cents}
              expiresAt={entitlements.book_credit_expires_at}
            />
          )}

          <p className="mt-6 font-sans text-sm text-bone-400 leading-relaxed max-w-2xl" data-testid="pricing-why-paid">
            No ads and no free tier to pay for — your subscription is the only thing that keeps the workbook running,
            so your entries are never the product.{" "}
            <Link to="/try" className="text-terracotta hover:text-bone-100 underline underline-offset-4" data-testid="pricing-try-link">
              Chapter One is open with no account.
            </Link>
          </p>
        </div>

        <PricingQuote />
        {!user && <MobileTryBar page="pricing" anchor='[data-testid="pricing-why-paid"]' />}

        {/* Tier grid — 3 columns on lg */}
        <div
          className="mt-10 sm:mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl"
          data-testid="pricing-tiers"
        >
          {TIERS.map((tier) => {
            const Icon = tier.icon;
            const disabled = busy !== null || alreadySubscribed;

            return (
              <div
                key={tier.key}
                data-testid={tier.testid}
                data-recommended={tier.highlight ? "true" : undefined}
                aria-label={tier.highlight ? `${tier.name} — recommended` : undefined}
                className={`relative flex flex-col p-6 sm:p-7 transition-[border-color,transform] ${
                  tier.highlight
                    ? "order-first md:order-none border-2 border-terracotta bg-terracotta/[0.09] shadow-[0_0_0_6px_rgba(182,110,80,0.12)] lg:scale-[1.04] lg:-translate-y-1 z-10"
                    : "border border-obsidian-600 bg-obsidian-800/40 hover:border-bone-500"
                }`}
              >
                {tier.highlight && (
                  <div className="absolute -top-3 left-6 bg-terracotta px-3 py-1 font-sans text-[0.6rem] uppercase tracking-[0.28em] text-obsidian-900" data-testid="pricing-recommended-label">
                    Recommended · Best value
                  </div>
                )}
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className={`h-9 w-9 flex items-center justify-center border ${
                      tier.highlight
                        ? "border-terracotta/60"
                        : "border-obsidian-600"
                    }`}
                  >
                    <Icon
                      className={`h-4 w-4 ${
                        tier.highlight ? "text-terracotta" : "text-bone-400"
                      }`}
                    />
                  </div>
                  <p
                    className={`font-sans uppercase tracking-[0.22em] text-[0.6rem] leading-tight ${
                      tier.highlight ? "text-terracotta" : "text-bone-400"
                    }`}
                  >
                    {tier.eyebrow}
                  </p>
                </div>

                <h3 className="font-serif text-2xl text-bone-100 mb-1">
                  {tier.name}
                </h3>
                <p className="font-serif text-4xl sm:text-5xl text-bone-100 leading-none mt-3">
                  {tier.price}
                </p>
                <p className="mt-2 font-sans text-xs uppercase tracking-[0.2em] text-bone-500">
                  {tier.cadence}
                </p>
                <p className="mt-3 font-serif italic text-sm text-bone-300 leading-snug" data-testid={`${tier.testid}-anchor`}>
                  {tier.anchor}
                </p>

                <p className="mt-5 font-sans text-sm text-bone-300 leading-relaxed">
                  {tier.tagline}
                </p>

                <ul className="mt-5 space-y-2.5 flex-1">
                  {tier.perks.map((perk, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2.5 font-sans text-sm text-bone-300 leading-relaxed"
                    >
                      <Check
                        className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
                          tier.highlight ? "text-terracotta" : "text-bone-500"
                        }`}
                      />
                      <span>{perk}</span>
                    </li>
                  ))}
                </ul>

                {tier.subnote && (
                  <p className="mt-4 font-sans text-[0.65rem] uppercase tracking-[0.2em] text-bone-500 border-t border-obsidian-600 pt-3">
                    {tier.subnote}
                  </p>
                )}

                <Button
                  onClick={() => startCheckout(tier.key)}
                  disabled={disabled}
                  data-testid={`${tier.testid}-cta`}
                  className={`mt-6 rounded-none py-5 font-sans uppercase tracking-wider text-xs transition-colors w-full ${
                    tier.highlight
                      ? "bg-terracotta hover:bg-terracotta-deep text-bone-100"
                      : "bg-transparent border border-bone-500 text-bone-100 hover:text-terracotta hover:border-terracotta"
                  } disabled:opacity-40`}
                >
                  {busy === tier.key ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  {alreadySubscribed ? "Already active" : tier.cta}
                  {!alreadySubscribed && <ArrowRight className="ml-2 h-4 w-4" />}
                </Button>
                <PlanRating badge={badge} id={tier.testid} />
              </div>
            );
          })}
        </div>

        <p className="mt-10 font-sans text-base text-bone-300" data-testid="pricing-free-path">
          Not ready to pay? Chapter One is open with no account —{" "}
          <Link to="/try" data-testid="pricing-free-path-link" className="text-terracotta underline underline-offset-4 hover:text-bone-100">try it free</Link>.
        </p>
        <p className="mt-3 font-sans text-sm text-bone-500" data-testid="pricing-lifetime-closed">
          Lifetime Access is closed to new members. Listeners who already bought it keep full access.
        </p>
        <PlanComparison />
        <ReviewBadgeStrip />

        {/* Reassurance strip */}
        <div className="mt-14 border-t border-obsidian-600 pt-10 grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-4xl">
          {[
            {
              t: "7-day cancel window",
              d: "Cancel within 7 days of any charge. Access continues to the end of the cycle — no more billing.",
            },
            {
              t: "One account, every device",
              d: "Sign in with Google on any device — your workbook syncs privately.",
            },
            {
              t: "Secured by Stripe",
              d: "Tax handled automatically. No cards stored on Umbra's servers.",
            },
          ].map((item, i) => (
            <div key={i}>
              <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-2">
                {String(i + 1).padStart(2, "0")}
              </p>
              <div className="font-serif text-lg text-bone-100 mb-1">
                {item.t}
              </div>
              <div className="font-sans text-sm text-bone-400 leading-relaxed">
                {item.d}
              </div>
            </div>
          ))}
        </div>

        {/* Back link */}
        <div className="mt-16 flex flex-wrap gap-3 sm:gap-6">
          <Link
            to="/"
            data-testid="pricing-back-link"
            className="inline-flex items-center min-h-[44px] px-3 sm:px-1 sm:min-h-0 py-2 sm:py-1 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-terracotta transition-colors border-b border-transparent hover:border-terracotta"
          >
            ← Back to the workbook
          </Link>
          <Link
            to="/faq"
            data-testid="pricing-faq-link"
            className="inline-flex items-center min-h-[44px] px-3 sm:px-1 sm:min-h-0 py-2 sm:py-1 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-terracotta transition-colors"
          >
            Pricing FAQ →
          </Link>
        </div>
      </div>
    </div>
  );
}

function BookCreditNudge({ cents, expiresAt }) {
  const amount = (cents / 100).toFixed(2);
  const days = expiresAt
    ? Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / (24 * 60 * 60 * 1000)))
    : null;
  const daysLabel = days === null ? null : days === 0 ? "expires today" : days === 1 ? "1 day left" : `${days} days left`;
  return (
    <div
      data-testid="pricing-book-credit-nudge"
      className="mt-8 border border-terracotta/60 bg-terracotta/10 p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5 max-w-2xl"
    >
      <div className="h-10 w-10 border border-terracotta text-terracotta flex items-center justify-center flex-shrink-0 font-serif text-lg">
        $
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-sans uppercase tracking-[0.22em] text-[0.65rem] text-terracotta mb-1">
          Book credit · <span data-testid="book-credit-amount">${amount}</span>
          {daysLabel && <> · {daysLabel}</>}
        </p>
        <p className="font-serif text-bone-100 leading-snug">
          Your book purchases become your first-month subscription — the credit
          applies automatically when you check out any plan below.
        </p>
      </div>
    </div>
  );
}

